import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import Switch from "react-switch";
import { useNavigate } from "react-router-dom";
import "./rules.css";
import "../themeupdate/themeupdate.css";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { ensureThemeJsonArray, isTrue } from "../../../functions/themeAssets";

// same limits as the PHP admin (rules.php)
const MAX_RULES = 6;
const MAX_RULE_LEN = 200;
const MAX_TITLE_LEN = 250;
const MAX_TAB_NAME_LEN = 80;
const MAX_THANKYOU_LEN = 250;
const MAX_QUESTIONS_TO_DISPLAY = 100;

const NAMED_COLORS = { black: "#000000", white: "#ffffff" };

/** <input type="color"> only accepts #rrggbb */
function toColorInputValue(val, fallback) {
  const v = String(val || "").trim();
  if (NAMED_COLORS[v.toLowerCase()]) return NAMED_COLORS[v.toLowerCase()];
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
  if (/^#[0-9a-fA-F]{8}$/.test(v)) return `#${v.slice(1, 7).toLowerCase()}`;
  if (/^#[0-9a-fA-F]{3}$/.test(v)) return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`.toLowerCase();
  return fallback;
}

const toInt = (v, fallback) => {
  const n = parseInt(String(v), 10);
  return Number.isNaN(n) ? fallback : n;
};

const COLOR_FIELDS = [
  { key: "text", label: "Text Color", theme: "landing_page_title_color", fallback: "#000000" },
  { key: "buttonText", label: "Button Text Color", theme: "button_text_color", fallback: "#ffffff" },
  { key: "box", label: "Button Color", theme: "questionbox_color", fallback: "#0000ff" },
  { key: "answer", label: "Selected Box Color", theme: "answer_color", fallback: "#0000ff" },
];

const OPTION_COLOR_FIELDS = [
  { key: "yes", label: "Yes", theme: "main_color1", fallback: "#ff0000" },
  { key: "maybe", label: "Maybe", theme: "main_color2", fallback: "#0000ff" },
  { key: "no", label: "No", theme: "main_color3", fallback: "#008000" },
];

const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [saving, setSaving] = useState(false);

  const [tabName, setTabName] = useState("");
  const [title, setTitle] = useState("");
  const [thankYou, setThankYou] = useState("");
  const [rules, setRules] = useState([""]);
  const [noOfQuestions, setNoOfQuestions] = useState("1");
  const [shuffle, setShuffle] = useState(true);
  const [thankYouEnabled, setThankYouEnabled] = useState(false);
  const [leaderboardEnabled, setLeaderboardEnabled] = useState(false);
  const [colors, setColors] = useState({});

  useEffect(() => {
    if (currentTheme) dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    const r = ensureThemeJsonArray(data.rules);
    setRules(r.length ? r.map(String) : [""]);
    setTabName(String(data.tab_name ?? "Know your Inclusion Quotient"));
    setTitle(String(data.landing_page_title ?? ""));
    setThankYou(String(data.custom_text_thank_you_page ?? ""));
    setNoOfQuestions(String(toInt(data.noOfQuestion, 1)));
    setShuffle(isTrue(data.shuffle));
    setThankYouEnabled(isTrue(data.thankyou_toggle));
    setLeaderboardEnabled(isTrue(data.leaderboard_toggle));
    const next = {};
    [...COLOR_FIELDS, ...OPTION_COLOR_FIELDS].forEach((f) => {
      next[f.key] = toColorInputValue(data[f.theme], f.fallback);
    });
    setColors(next);
  }, [data]);

  const payloadBase = () => ({
    organizationId: organizationId || "admin",
    sessionId: sessionId || "admin",
    currentTheme,
  });

  const handleRuleChange = (idx, val) => {
    const arr = [...rules];
    arr[idx] = val;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= MAX_RULES) {
      Swal.fire("Limit", `Keep ${MAX_RULES} rules at the most.`, "warning");
      return;
    }
    setRules([...rules, ""]);
  };

  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      Swal.fire("Required", "At least 1 rule is required.", "warning");
      return;
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  // the two switches save on their own, like the PHP page did when they were clicked
  const saveToggle = async (key, value, apply) => {
    apply(value);
    try {
      await updateThemeData({
        payload: { data: { [key]: value }, ...payloadBase() },
        token: adminToken,
      });
    } catch (e) {
      apply(!value);
      Swal.fire("Error", e.message || "Failed to save", "error");
    }
  };

  const validate = () => {
    const trimmedRules = rules.map((r) => r.trim());
    if (trimmedRules.length === 0) return "At least 1 rule is required.";
    if (trimmedRules.some((r) => !r)) return "Please fill in all rule fields.";
    if (trimmedRules.some((r) => r.length > MAX_RULE_LEN)) return `Rules must be ${MAX_RULE_LEN} characters or less.`;
    if (!tabName.trim()) return "Tab name cannot be empty.";
    if (tabName.length > MAX_TAB_NAME_LEN) return `The tab name can be at most ${MAX_TAB_NAME_LEN} characters.`;
    if (!title.trim()) return "Title can't be empty";
    if (title.length > MAX_TITLE_LEN) return `You exceed the maximum limit of ${MAX_TITLE_LEN} characters for the title!`;
    if (thankYou.length > MAX_THANKYOU_LEN) return "You exceed the maximum limit of thank you page! ";
    if (!/^\d+$/.test(String(noOfQuestions).trim()) || parseInt(noOfQuestions, 10) < 1) {
      return "You must display at least 1 question.";
    }
    if (parseInt(noOfQuestions, 10) > MAX_QUESTIONS_TO_DISPLAY) {
      return `You can display at most ${MAX_QUESTIONS_TO_DISPLAY} questions.`;
    }
    return null;
  };

  const handleSave = async () => {
    const problem = validate();
    if (problem) {
      Swal.fire("Validation", problem, "warning");
      return;
    }
    setSaving(true);
    try {
      await updateThemeData({
        payload: {
          data: {
            rules: rules.map((r) => r.trim()),
            tab_name: tabName.trim(),
            landing_page_title: title,
            custom_text_thank_you_page: thankYou,
            noOfQuestion: parseInt(noOfQuestions, 10),
            shuffle,
            thankyou_toggle: thankYouEnabled,
            leaderboard_toggle: leaderboardEnabled,
            landing_page_title_color: colors.text,
            button_text_color: colors.buttonText,
            questionbox_color: colors.box,
            answer_color: colors.answer,
            main_color1: colors.yes,
            main_color2: colors.maybe,
            main_color3: colors.no,
          },
          ...payloadBase(),
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme, isAdmin: true })).unwrap();
      await Swal.fire({ title: "Data Updated", icon: "success", timer: 1000, showConfirmButton: false });
      navigate("/admin/editor");
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  const colorRow = (f) => (
    <div className="rules-php-marks-row rules-php-color-row" key={f.key}>
      <label htmlFor={`color-${f.key}`} className="rules-php-marks-label">
        {f.label}
      </label>
      <input
        id={`color-${f.key}`}
        type="color"
        className="themeupdate-color-input rules-php-color-input"
        value={colors[f.key] || f.fallback}
        onChange={(e) => setColors((c) => ({ ...c, [f.key]: e.target.value }))}
      />
    </div>
  );

  if (status === "loading" && !data) {
    return (
      <div className="rules-page-new">
        <div className="themeupdate-loading">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin")}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      <div className="themeupdate-row rules-php-layout">
        <div className="themeupdate-col rules-php-col">
          <div className="themeupdate-card rules-php-card rules-php-card--left">
            <div className="themeupdate-card-body rules-php-card-body">
              <h4 className="themeupdate-label rules-php-card-title">Tab Name:</h4>
              <input
                type="text"
                className="themeupdate-input"
                maxLength={MAX_TAB_NAME_LEN}
                value={tabName}
                onChange={(e) => setTabName(e.target.value)}
              />
              <p className="rules-note">
                * This name is shown on the browser tab. * Maximum character limit should be {MAX_TAB_NAME_LEN} characters.
              </p>

              <h4 className="themeupdate-label rules-php-card-title" style={{ marginTop: 16 }}>
                Add Custom Title:
              </h4>
              <textarea
                className="themeupdate-input rules-php-textarea"
                maxLength={MAX_TITLE_LEN}
                value={title}
                onChange={(e) => setTitle(e.target.value.replace(/^\s+/, ""))}
              />
              <p className="rules-note">
                * Maximum character limit should be {MAX_TITLE_LEN} characters. * This title will reflect on the main
                game page.
              </p>

              <h4 className="themeupdate-label rules-php-card-title" style={{ marginTop: 16 }}>
                Add Custom Rules
              </h4>
              <div className="themeupdate-rules-repeater">
                {rules.map((rule, i) => (
                  <div key={i} className="themeupdate-rule-row rules-php-repeater-row">
                    <textarea
                      className="themeupdate-input rules-php-textarea rules-php-repeater-textarea"
                      placeholder="Rules"
                      value={rule}
                      onChange={(e) => handleRuleChange(i, e.target.value)}
                    />
                    <button
                      type="button"
                      className="themeupdate-btn themeupdate-btn-danger"
                      onClick={() => handleRemoveRule(i)}
                    >
                      <i className="fa-solid fa-times" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="themeupdate-btn themeupdate-btn-primary themeupdate-add-rule"
                  onClick={handleAddRule}
                >
                  <i className="fa-solid fa-plus" /> Add new rule
                </button>
                <p className="rules-note">
                  <code>* Keep {MAX_RULES} rules at the most</code>
                  <br />
                  <code>* At least 1 rule is required</code>
                  <br />
                  <code>* Character limit of each rule is {MAX_RULE_LEN}</code>
                </p>
              </div>

              <h4 className="themeupdate-label rules-php-card-title" style={{ marginTop: 16 }}>
                Custom Text thank you page:
              </h4>
              <input
                type="text"
                className="themeupdate-input"
                maxLength={MAX_THANKYOU_LEN}
                value={thankYou}
                onChange={(e) => setThankYou(e.target.value)}
              />
              <p className="rules-note">
                * This text will reflect on the thank you page. * Character limit is {MAX_THANKYOU_LEN}.
              </p>
            </div>
          </div>
        </div>

        <div className="themeupdate-col rules-php-col">
          <div className="themeupdate-card rules-php-card rules-php-card--right">
            <div className="themeupdate-card-body rules-php-card-body">
              <h4 className="themeupdate-label rules-php-card-title">Page Content</h4>

              <div className="rules-php-marks-row">
                <label htmlFor="no-of-questions" className="rules-php-marks-label">
                  No of questions to display
                </label>
                <input
                  id="no-of-questions"
                  type="number"
                  min="1"
                  max={MAX_QUESTIONS_TO_DISPLAY}
                  step="1"
                  className="themeupdate-input rules-php-number"
                  value={noOfQuestions}
                  onKeyDown={(e) => ["e", "E", "+", "-", ".", ","].includes(e.key) && e.preventDefault()}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/[^0-9]/g, "").replace(/^0+/, "");
                    const clamped =
                      digits && parseInt(digits, 10) > MAX_QUESTIONS_TO_DISPLAY
                        ? String(MAX_QUESTIONS_TO_DISPLAY)
                        : digits;
                    setNoOfQuestions(clamped);
                  }}
                  onBlur={() => {
                    if (!noOfQuestions || parseInt(noOfQuestions, 10) < 1) setNoOfQuestions("1");
                    else if (parseInt(noOfQuestions, 10) > MAX_QUESTIONS_TO_DISPLAY) {
                      setNoOfQuestions(String(MAX_QUESTIONS_TO_DISPLAY));
                    }
                  }}
                />
              </div>
              <p className="rules-note">* Maximum {MAX_QUESTIONS_TO_DISPLAY} questions.</p>

              <div className="rules-php-marks-row">
                <label className="rules-php-marks-label">Shuffle Questions</label>
                <Switch
                  checked={shuffle}
                  onChange={(v) => saveToggle("shuffle", v, setShuffle)}
                  onColor="#4caf50"
                />
              </div>

              <div className="rules-php-marks-row">
                <label className="rules-php-marks-label">Enable Thank you message</label>
                <Switch
                  checked={thankYouEnabled}
                  onChange={(v) => saveToggle("thankyou_toggle", v, setThankYouEnabled)}
                  onColor="#4caf50"
                />
              </div>
              {thankYouEnabled && (
                <div className="rules-php-marks-row">
                  <button
                    type="button"
                    className="themeupdate-btn themeupdate-btn-primary"
                    onClick={() => navigate("/admin/thankyou")}
                  >
                    Thank you message customization
                  </button>
                </div>
              )}

              <div className="rules-php-marks-row">
                <label className="rules-php-marks-label">Show Leaderboard</label>
                <Switch
                  checked={leaderboardEnabled}
                  onChange={(v) => saveToggle("leaderboard_toggle", v, setLeaderboardEnabled)}
                  onColor="#4caf50"
                />
              </div>
              <p className="rules-note">
                * When on, the thank you page shows the top 5 scores (plus the player's own rank if they're not in
                the top 5) instead of their score.
              </p>

              {COLOR_FIELDS.map(colorRow)}

              <p className="rules-note">These colors will reflect in 3 categories of Yes, Maybe and No:</p>
              {OPTION_COLOR_FIELDS.map(colorRow)}
            </div>
          </div>
        </div>
      </div>

      <div className="rules-action-holder">
        <button type="button" className="save-and-continue" onClick={handleSave} disabled={saving}>
          Next
        </button>
      </div>
    </>
  );
};

export default Rules;
